import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { companyOpportunityAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeft, LogOut, User, MapPin, Briefcase,
  Loader2, CheckCircle, Star, BookmarkCheck
} from 'lucide-react';

const STATUS_CONFIG = {
  APPLIED:      { label: 'Applied',      color: 'bg-blue-100 text-blue-700'    },
  SHORTLISTED:  { label: 'Shortlisted',  color: 'bg-yellow-100 text-yellow-700'},
  SELECTED:     { label: 'Selected',     color: 'bg-green-100 text-green-700'  },
  REJECTED:     { label: 'Rejected',     color: 'bg-red-100 text-red-600'      },
  WITHDRAWN:    { label: 'Withdrawn',    color: 'bg-gray-100 text-gray-500'    },
};

export default function OpportunityApplicants() {
  const { id } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    try {
      const res = await companyOpportunityAPI.getApplicants(id);
      setApplicants(res.data.data || []);
    } catch { toast.error('Failed to load applicants'); }
    finally { setLoading(false); }
  };

  const handleShortlist = async (appId) => {
    setActionLoading(appId);
    try {
      await companyOpportunityAPI.shortlist(appId);
      setApplicants(a => a.map(x =>
        x.id === appId ? { ...x, status: 'SHORTLISTED' } : x));
      toast.success('Candidate shortlisted');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleSelect = async (appId) => {
    setActionLoading(appId);
    try {
      await companyOpportunityAPI.selectApplicant(appId);
      toast.success('Staff selected! Placement created automatically.');
      setShowConfirm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to select'); }
    finally { setActionLoading(null); }
  };

  const active = applicants.filter(a => !['REJECTED', 'WITHDRAWN'].includes(a.status));
  const others = applicants.filter(a => ['REJECTED', 'WITHDRAWN'].includes(a.status));
  const hasSelection = applicants.some(a => a.status === 'SELECTED');

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/opportunities')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
          </div>
          <button onClick={() => { logout(); navigate('/company/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-800">Applicants</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {active.length} active applicant{active.length !== 1 ? 's' : ''}
          </p>
        </div>

        {hasSelection && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
            <p className="text-green-800 text-sm font-semibold">
              A candidate has been selected. The placement has been created and this opportunity is now filled.
            </p>
          </div>
        )}

        {applicants.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <User className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 font-semibold">No applications yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {[...active, ...others].map(app => {
              const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.APPLIED;
              const canAct = !hasSelection && !['REJECTED', 'WITHDRAWN', 'SELECTED'].includes(app.status);
              return (
                <div key={app.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600
                        rounded-xl flex items-center justify-center text-white font-black shrink-0">
                        {app.staffFirstName?.charAt(0)}{app.staffLastName?.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-bold text-slate-800">
                            {app.staffFirstName} {app.staffLastName}
                          </h3>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.color}`}>
                            {cfg.label}
                          </span>
                          {app.placementStatus === 'HIRED' && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                              Currently Hired
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 text-xs text-gray-500">
                          {app.staffTypeName && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{app.staffTypeName}</span>}
                          {app.staffCity && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{app.staffCity}{app.staffState ? `, ${app.staffState}` : ''}</span>}
                          {app.experienceYears != null && <span>{app.experienceYears} yrs exp</span>}
                          {app.qualification && <span>{app.qualification}</span>}
                        </div>
                        {app.coverNote && (
                          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 mt-2">
                            "{app.coverNote}"
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Applied {new Date(app.createdAt).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>

                    {canAct && (
                      <div className="flex flex-col gap-2 shrink-0">
                        {app.status === 'APPLIED' && (
                          <button
                            onClick={() => handleShortlist(app.id)}
                            disabled={actionLoading === app.id}
                            className="flex items-center gap-1.5 bg-yellow-50 text-yellow-700 border border-yellow-200
                              px-4 py-2 rounded-xl text-xs font-bold hover:bg-yellow-100 transition disabled:opacity-50">
                            {actionLoading === app.id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <BookmarkCheck className="h-3.5 w-3.5" />}
                            Shortlist
                          </button>
                        )}
                        <button
                          onClick={() => setShowConfirm(app.id)}
                          disabled={actionLoading === app.id || app.placementStatus === 'HIRED'}
                          className="flex items-center gap-1.5 bg-green-600 text-white
                            px-4 py-2 rounded-xl text-xs font-bold hover:bg-green-700 transition
                            disabled:opacity-50 shadow-sm">
                          <CheckCircle className="h-3.5 w-3.5" /> Select
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirm select modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-slate-800 mb-2">Confirm Selection</h3>
            <p className="text-sm text-gray-500 mb-5">
              Selecting this candidate will create a placement immediately and close this opportunity.
              All other applicants will be rejected. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirm(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl font-semibold text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handleSelect(showConfirm)}
                disabled={actionLoading === showConfirm}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-xl font-bold text-sm hover:bg-green-700 disabled:opacity-60">
                {actionLoading === showConfirm ? 'Processing...' : 'Confirm Selection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
